import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Storefront, HourglassBottom, Verified, AccountBalanceWallet } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { dealersApi } from '../../api/dealers.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const num = (n = 0) => Number(n).toLocaleString('en-IN');

export function DealerDashboardPage() {
  const dash = useQuery({ queryKey: ['dealers', 'dashboard'], queryFn: () => dealersApi.dashboard() });
  const d = dash.data;

  const statusData = d ? Object.entries(d.byStatus).map(([label, value]) => ({ label: label.replace(/_/g, ' '), value, color: '#1e40af' })) : [];
  const typeData = d ? Object.entries(d.byType).map(([label, value]) => ({ label, value, color: '#0d9488' })) : [];

  return (
    <>
      <PageHeader title="Dealer Dashboard" subtitle="EVCPD dealer onboarding pipeline, mix and credit exposure." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Total Dealers" value={num(d?.total)} icon={<Storefront />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#d97706" label="Pending Approval" value={num(d?.pendingApproval)} icon={<HourglassBottom />} hint={`${num(d?.drafts)} drafts`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Approved" value={num(d?.approved)} icon={<Verified />} hint={`${num(d?.rejected)} rejected`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#7c3aed" label="Credit Exposure" value={inr(d?.creditExposure)} icon={<AccountBalanceWallet />} hint="Approved dealers" /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Dealers by Status" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>{statusData.length ? <MiniBarChart data={statusData} valueFormatter={num} /> : <EmptyState title="No dealers yet" dense />}</CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Dealers by Type" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>{typeData.length ? <MiniBarChart data={typeData} valueFormatter={num} /> : <EmptyState title="No dealers yet" dense />}</CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Recent Registrations" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(d?.recent.length ?? 0) === 0 ? <EmptyState title="No dealers yet" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Dealer</TableCell><TableCell>Type</TableCell><TableCell align="right">Credit Limit</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(d?.recent ?? []).map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.dealerCode}</TableCell>
                        <TableCell>{r.dealerName}</TableCell>
                        <TableCell>{r.dealerType}</TableCell>
                        <TableCell align="right">{inr(r.creditLimit)}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
